import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import styles from './TodayRoutePopup.module.css';
import moment from 'moment';
import 'moment/locale/tr';
import { Navigation2, MapPin, X } from 'lucide-react';
import { IAppointment } from '../../../../models/appointment/AppointmentModel';
import { toast } from 'react-toastify';

moment.locale('tr');

const TodayRoutePopup = () => {
  const { popupStore, appointmentStore } = useStore();
  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!popupStore.isTodayRoutePopupOpen) return;
    setExcludedIds(new Set());
    setLoading(true);
    appointmentStore
      .getTodayAppointmentsForUser()
      .then(data => {
        setAppointments(data || []);
      })
      .catch(() => {
        setAppointments([]);
        toast.error('Bugünkü randevular yüklenirken bir hata oluştu.');
      })
      .finally(() => setLoading(false));
  }, [popupStore.isTodayRoutePopupOpen]);

  const handleClose = () => {
    popupStore.setIsTodayRoutePopupOpen(false);
    setAppointments([]);
    setExcludedIds(new Set());
  };

  const handleRemoveFromRoute = (id: number) => {
    setExcludedIds(prev => new Set(prev).add(id));
  };

  const visibleAppointments = appointments.filter(a => !excludedIds.has(a.id!));

  const handleOpenGoogleMaps = () => {
    const withLocation = visibleAppointments.filter(
      a => a.hospitalLocation && a.hospitalLocation.trim() !== ''
    );
    if (withLocation.length === 0) {
      toast.warning('Haritada gösterilecek adres bulunamadı.');
      return;
    }

    const addresses = withLocation.map(a => a.hospitalLocation!);
    let googleMapsUrl: string;

    if (addresses.length === 1) {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addresses[0])}&travelmode=driving`;
    } else {
      const destination = encodeURIComponent(addresses[addresses.length - 1]);
      const waypoints = addresses.slice(0, -1).map(a => encodeURIComponent(a)).join('|');
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
    }

    window.open(googleMapsUrl, '_blank');
  };

  const hasAnyLocation = visibleAppointments.some(
    a => a.hospitalLocation && a.hospitalLocation.trim() !== ''
  );

  return (
    <Popup
      isOpen={popupStore.isTodayRoutePopupOpen}
      onClose={handleClose}
      title="Bugünkü Randevu Rotaları"
      description="Bugünkü randevu rotalarını görüntüleyin"
      variant="info"
      icon={<MapPin size={22} strokeWidth={2} />}
    >
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loading}>Yükleniyor...</div>
        ) : appointments.length === 0 ? (
          <div className={styles.empty}>
            <MapPin size={48} className={styles.emptyIcon} />
            <p>Bugün için güncel saatten sonra randevunuz bulunmuyor.</p>
          </div>
        ) : (
          <>
            <p className={styles.subtitle}>
              Güncel saatten sonraki randevularınız (sırayla):
            </p>
            <ul className={styles.routeList}>
              {visibleAppointments.map((apt, index) => (
                <li key={apt.id} className={styles.routeItem}>
                  <span className={styles.order}>{index + 1}</span>
                  <div className={styles.routeContent}>
                    <span className={styles.hospitalName}>{apt.hospitalName || '-'}</span>
                    <span className={styles.time}>
                      {apt.appointmentStartTime}
                      {apt.appointmentEndTime ? ` - ${apt.appointmentEndTime}` : ''}
                    </span>
                    {apt.hospitalLocation && (
                      <span className={styles.address}>{apt.hospitalLocation}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFromRoute(apt.id!)}
                    className={styles.removeBtn}
                    title="Rota durağından çıkar"
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
            {hasAnyLocation && (
              <button
                onClick={handleOpenGoogleMaps}
                className={styles.mapButton}
                title="Konuma Git"
              >
                <Navigation2 size={18} />
                <span>Konuma Git (Rotalı)</span>
              </button>
            )}
          </>
        )}
      </div>
    </Popup>
  );
};

export default observer(TodayRoutePopup);
